/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './FavoriteHeart.scss';

import * as React from 'react';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import axios from 'axios';

import { useToast, Button, type ButtonProps, URLHelper } from 'sarsaparilla';
import { getAuthHeader, isRegisteredUserLoggedIn } from 'sarsaparilla/auth';

import { authModalActions } from 'shared-ui/auth/redux';
import { useTypedDispatch } from 'shared-ui/redux';

import { FavoriteHeartAddRemoveModal } from './FavoriteHeartAddRemoveModal';
import { FavoriteHeartConfirmModal } from './FavoriteHeartConfirmModal';

export interface FavoriteHeartProps {
    // example: asset_id
    entityId: string; // Required

    // example: "permit" or "camping"
    entityType?: string;

    // example: facility_id
    parentId?: string;

    // example: "campground"
    parentType?: string;

    // example: Some Specific National Wildlife Refuge
    favAriaLabel?: string;

    // shows the text "Favorite" next to the icon
    showFavoriteText?: boolean;

    // function for modal
    onSave?: () => void;

    // function for modal
    onCancel?: () => void;

    /**
     *  opens modal to allow user to edit favorites.
     *  Otherwise default confirmation modal is shown.
     */
    editable?: boolean;

    /** Button visual appearance. Default is 'subtle'. */
    appearance?: ButtonProps['appearance'];

    /** Button size (height / min-width). Default is 'lg'. */
    size?: 'sm' | 'md' | 'lg';

    /** GA tracking id for the favorite button. */
    gaTrackingId?: ButtonProps['gaTrackingId'];

    /** GA tracking value for the favorite button. */
    gaTrackingValue?: ButtonProps['gaTrackingValue'];

    className?: string;
}

type FavoritesGroupsResponseResultItem = {
    account_id: string;
    created_date: string;
    group_id: string;
    group_name: string;
    is_active: boolean;
    is_primary: boolean;
    updated_date: string;
    favorites: {
        account_id: string;
        created_date: string;
        entity_id: string;
        entity_type: string;
        group_id: string;
        is_active: boolean;
        updated_date: string;
    }[];
};

export function FavoriteHeart({
    className,
    entityId,
    entityType,
    parentId,
    parentType,
    showFavoriteText,
    onSave,
    onCancel,
    editable,
    favAriaLabel,
    appearance = 'subtle',
    size = 'lg',
    gaTrackingId,
    gaTrackingValue,
}: FavoriteHeartProps) {
    const [isInitialized, setInitialized] = React.useState(false);
    const [liked, setLiked] = React.useState(false);
    const [loggedIn, setLoggedIn] = React.useState(false);
    const [removing, setRemoving] = React.useState(false);
    const [showAddFavoritesModal, setShowAddFavoritesModal] = React.useState(false);
    const [showRemoveFavoritesModal, setShowRemoveFavoritesModal] = React.useState(false);
    const [hasErrors, setHasErrors] = React.useState(false);
    const dispatch = useTypedDispatch();
    const auth = useSelector((state: any) => state?.auth);
    const showToast = useToast();
    const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
    const openConfirmModal = () => setIsConfirmModalOpen(true);
    const closeConfirmModal = () => setIsConfirmModalOpen(false);
    const loadEntityFavorites = React.useCallback(async () => {
        if (!loggedIn || !entityId) {
            return;
        }
        try {
            const url = URLHelper.urlWithParams(
                `${process.env.API}/trips/favorites/entity`,
                {
                    entity_id: entityId,
                    entity_type: entityType || 'asset',
                }
            );
            const res = await axios.get(url, {
                headers: { Authorization: getAuthHeader() },
            });
            setLiked((res.data.result?.length || 0) > 0);
            setHasErrors(false);
        } catch {
            setHasErrors(true);
        }
        setInitialized(true);
    }, [entityId, entityType, loggedIn]);

    React.useEffect(() => {
        setLoggedIn(isRegisteredUserLoggedIn());
        if (!isRegisteredUserLoggedIn()) {
            setInitialized(true);
        }
    }, [auth]);

    React.useEffect(() => {
        loadEntityFavorites();
    }, [entityId, entityType, loadEntityFavorites, loggedIn]);

    const addToFavorites = () => {
        setShowAddFavoritesModal(true);
    };

    const save = () => {
        loadEntityFavorites();
        setShowAddFavoritesModal(false);
        setShowRemoveFavoritesModal(false);
        if (onSave) {
            onSave();
        }
    };

    const cancel = () => {
        setShowAddFavoritesModal(false);
        setShowRemoveFavoritesModal(false);
        if (onCancel) {
            onCancel();
        }
    };

    // removes favorite item from all favorite groups
    const removeFromFavorites = async () => {
        setRemoving(true);
        let groups: FavoritesGroupsResponseResultItem[] = [];
        try {
            const url = `${process.env.API}/trips/favorites/groups`;
            const res = await axios.get(url, {
                headers: { Authorization: getAuthHeader() },
            });
            groups = res?.data?.result || [];
        } catch {
            setRemoving(false);
            cancel();
            return;
        }
        const groupsToUpdate = groups.filter((g) => {
            const filtered = g.favorites?.filter(
                (f) => f.entity_id === entityId && f.entity_type === entityType
            );
            return filtered?.length > 0;
        });
        const updated = groupsToUpdate.map((g) => {
            const group = g;
            const filtered = g.favorites?.map((f) => {
                const fav = f;
                if (f.entity_id === entityId && f.entity_type === entityType) {
                    fav.is_active = false;
                }
                return fav;
            });
            group.favorites = filtered;
            return group;
        });
        // save groups
        try {
            const url = `${process.env.API}/trips/favorites/group`;
            for (let i = 0; i < updated.length; i += 1) {
                // eslint-disable-next-line no-await-in-loop
                await axios.post(url, updated[i], {
                    headers: { Authorization: getAuthHeader() },
                });
            }
        } catch {
            setRemoving(false);
            cancel();
            return;
        }
        setRemoving(false);

        showToast('Removed from Favorites.');
        save();
    };

    const confirmRemoveFromFavorites = () => {
        closeConfirmModal();
        removeFromFavorites();
    };

    const toggleHeart = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!isRegisteredUserLoggedIn()) {
            dispatch(authModalActions.openLoginModal());
            return;
        }
        if (liked) {
            if (editable) {
                setShowRemoveFavoritesModal(true);
            } else {
                openConfirmModal();
            }
        } else {
            addToFavorites();
        }
    };

    const heartClass = liked ? 'rec-favorite-heart-liked' : 'rec-favorite-heart-unliked';

    /**
     * Provides aria label to favorite button for screen reader users.
     * If favAriaLabel is passed in, the card title is used. Otherwise it sets the default terminology.
     */
    const favoriteAriaLabel = () => {
        let favoriteText = liked ? 'Remove From Favorites' : `Add To Favorites`;

        if (favAriaLabel) {
            favoriteText += `: ${favAriaLabel}`;
        }
        return favoriteText;
    };

    if (hasErrors) {
        return null;
    }

    return (
        <>
            <div className={cx('rec-favorite-heart-wrapper', className)}>
                <Button
                    appearance={appearance}
                    size={size}
                    className={`rec-favorite-heart ${heartClass} p-0`}
                    iconBeforeElement={<HeartIcon />}
                    aria-label={favoriteAriaLabel()}
                    onClick={toggleHeart}
                    isLoading={removing}
                    isDisabled={!isInitialized}
                    gaTrackingId={gaTrackingId}
                    gaTrackingValue={gaTrackingValue}
                >
                    {showFavoriteText ? (
                        <span className="rec-favorite-text">Favorite</span>
                    ) : null}
                </Button>
            </div>
            {(showAddFavoritesModal || showRemoveFavoritesModal) && (
                <FavoriteHeartAddRemoveModal
                    entityId={entityId}
                    entityType={entityType || 'asset'}
                    parentId={parentId}
                    parentType={parentType}
                    mode={`${showAddFavoritesModal ? 'add' : 'edit'}`}
                    onSave={save}
                    onCancel={cancel}
                />
            )}
            <FavoriteHeartConfirmModal
                closeModal={closeConfirmModal}
                onConfirmClick={confirmRemoveFromFavorites}
                isOpen={isConfirmModalOpen}
            />
        </>
    );
}

/**
 * FavoriteHeart can not use the standard <Icon> component because it applies css to the
 * individual paths inside the svg
 */
function HeartIcon() {
    return (
        <svg
            id="rec-icon-favorite-border"
            viewBox="0 0 24 24"
            role="presentation"
            focusable="false"
            width={24}
            height={24}
        >
            <path
                fill="none"
                d="M12 24l-2.8-2.5C3.8 16.7 0 13.2 0 8.5 0 4.3 3.3 1 7.5 1c1.6 0 3.2.5 4.5 1.4 1.3-.9 2.9-1.4 4.5-1.4C20.7 1 24 4.3 24 8.5c0 4.7-3.9 8.2-9.2 13L12 24z"
            />
            <path
                fill="none"
                d="M16.5 3c-1.7 0-3.4.8-4.5 2.1C10.9 3.8 9.2 3 7.5 3 4.4 3 2 5.4 2 8.5c0 3.8 3.4 6.9 8.6 11.5l1.4 1.3 1.4-1.3c5.2-4.7 8.6-7.8 8.6-11.5C22 5.4 19.6 3 16.5 3z"
            />
            <path d="M16.5 3c-1.7 0-3.4.8-4.5 2.1C10.9 3.8 9.2 3 7.5 3 4.4 3 2 5.4 2 8.5c0 3.8 3.4 6.9 8.6 11.5l1.4 1.3 1.4-1.3c5.2-4.7 8.6-7.8 8.6-11.5C22 5.4 19.6 3 16.5 3zm-4.4 15.5l-.1.1-.1-.1C7.1 14.2 4 11.4 4 8.5 4 6.5 5.5 5 7.5 5c1.5 0 3 1 3.6 2.4H13C13.5 6 15 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.9-3.1 5.7-7.9 10z" />
        </svg>
    );
}
